import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { DNC_ON_BADGE, dncConfirmBody, dncConfirmTitle, dncStateLine } from '../../lib/leadBot/doNotContactCopy';
import { getDoNotContact, setDoNotContactFlag } from './api';

// "Do not contact" for one conversation's lead at that club. The state is read live; both directions go
// through a confirmation and are audited server-side with who did it.

export function DoNotContactControl({ conversationId, clubName }: { conversationId: string; clubName: string | null }) {
  const queryClient = useQueryClient();
  const [target, setTarget] = useState<boolean | null>(null);
  const key = ['lead-bot', 'do-not-contact', conversationId];
  const { data } = useQuery({ queryKey: key, queryFn: () => getDoNotContact(conversationId) });

  const change = useMutation({
    mutationFn: (set: boolean) => setDoNotContactFlag(conversationId, set),
    onSuccess: (_r, set) => {
      toast.success(set ? 'Marked do not contact.' : 'Contact allowed again.');
      setTarget(null);
      void queryClient.invalidateQueries({ queryKey: key });
    },
    onError: (err: Error) => toast.error(err.message),
  });

  if (!data) return null;
  const isSet = data.doNotContact;
  return (
    <>
      {isSet && <Badge variant="danger" title={dncStateLine(true, data.record?.setByName ?? null, data.record?.setAt ?? null)}>{DNC_ON_BADGE}</Badge>}
      <Button variant="outline" size="sm" onClick={() => setTarget(!isSet)}>
        {isSet ? 'Allow contact again' : 'Do not contact'}
      </Button>
      <Dialog open={target !== null} onOpenChange={(open) => !open && setTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{dncConfirmTitle(target === true)}</DialogTitle>
            <DialogDescription>{dncConfirmBody(target === true, clubName)}</DialogDescription>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">{dncStateLine(isSet, data.record?.setByName ?? null, data.record?.setAt ?? null)}</p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTarget(null)}>Cancel</Button>
            <Button variant={target ? 'destructive' : 'default'} disabled={change.isPending} onClick={() => target !== null && change.mutate(target)}>
              {target ? 'Mark do not contact' : 'Allow contact'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
